/*
 * SPELLROO GAMING — quick add to cart (no drawer pop-up).
 *
 * Replaces Ella's add-to-cart flow for [data-btn-addtocart] buttons on product cards,
 * the product page (main + sticky bar) and quick view.
 *   Ella:  add.js -> cart.js -> open drawer -> GET /cart?view=ajax_side_cart (3–4 serial requests,
 *          ~2–3 s, and the drawer covers the page after every card).
 *   Here:  the card image flies to the header cart icon straight away and the count goes up; a
 *          "✓ n in cart" line shows next to the price (cards) or under the button (product page).
 *          The drawer stays closed and is re-rendered only when the customer opens it.
 *
 * Cart writes never overlap (Shopify can lose updates when they do). Each request is the
 * single-item form {id, quantity} so the Pify order-limit app can check it, one per click, sent
 * in order in the background while the count and "in cart" line update straight away.
 *
 * Left to Ella on purpose: notify-me / sold-out buttons, gift cards with a recipient form, the
 * /cart page, and any button without a variant id.
 * Plain JS, no jQuery. Loaded with defer from snippets/sr-quick-add.liquid.
 */
(function () {
  if (window.srQuickAdd) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var STR = window.srQuickAddStrings || {};
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var state = {
    serverCount: null,   // item_count as last confirmed by Shopify
    qty: {},             // variant_id -> quantity in cart (confirmed)
    jobs: [],            // adds not yet confirmed (shown optimistically once the image lands)
    waiting: [],         // adds not yet sent
    inflight: false,
    loaded: null,
    idleWaiters: [],
    drawerDirty: false,
    drawerSeen: false    // Ella fetches and renders the drawer itself on the first icon click
  };

  /* ---------- helpers ---------- */

  function headers(json) {
    var h = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    if (json) h['Content-Type'] = 'application/json';
    return h;
  }

  function getCart() {
    return fetch(ROOT + 'cart.js', { headers: headers(), credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); });
  }

  function readCount() {
    var el = document.querySelector('[data-cart-count]');
    var n = el ? parseInt(el.textContent, 10) : NaN;
    return isNaN(n) ? 0 : n;
  }

  function shownCount() {
    // Until /cart.js has answered, start from the number Liquid printed at page load (captured
    // once in start()). Never re-read the DOM here: it already includes our optimistic adds.
    var n = state.serverCount == null ? (state.baseCount || 0) : state.serverCount;
    state.jobs.forEach(function (j) { n += j.shown; });
    return n;
  }

  function renderCount() {
    var n = shownCount();
    var many = window.cartStrings && window.cartStrings.item_99;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      var v = n >= 100 && many && el.closest('.cart-count-bubble') ? many : String(n);
      if (el.textContent !== v) el.textContent = v;
    });
    if (window.cartStrings) {
      document.querySelectorAll('[data-cart-text]').forEach(function (el) {
        el.textContent = n === 1 ? window.cartStrings.item : window.cartStrings.items;
      });
    }
    // Screen-reader text in the bubble ("3 items"); Liquid prints it once at page load.
    var sr = n === 1 ? (STR.countOne || '1 item') : (STR.countOther || '[count] items').replace('[count]', n);
    document.querySelectorAll('.cart-count-bubble > .visually-hidden').forEach(function (el) {
      if (el.textContent !== sr) el.textContent = sr;
    });
  }

  function qtyMap(cart) {
    var m = {};
    (cart.items || []).forEach(function (it) { m[it.variant_id] = (m[it.variant_id] || 0) + it.quantity; });
    return m;
  }

  function applyCart(cart) {
    state.qty = qtyMap(cart);
    state.serverCount = cart.item_count;
    renderCount();
    badges.refreshAll();
  }

  function ensureLoaded() {
    if (!state.loaded) {
      state.loaded = getCart().then(applyCart).catch(function () {
        state.loaded = null;   // try again on the next add
      });
    }
    return state.loaded || Promise.resolve();
  }

  function busy() { return state.inflight || state.waiting.length > 0; }

  function whenIdle() {
    if (!busy()) return Promise.resolve();
    return new Promise(function (resolve) { state.idleWaiters.push(resolve); });
  }

  function formOf(btn) { return btn.form || btn.closest('form'); }

  function variantIdOf(btn) {
    var form = formOf(btn);
    var input = form && form.querySelector('[name="id"]');
    var id = input ? parseInt(input.value, 10) : NaN;
    return isNaN(id) ? null : id;
  }

  function quantityOf(btn) {
    var form = formOf(btn);
    var input = form && form.querySelector('[name="quantity"]');
    var q = input ? parseInt(input.value, 10) : 1;
    return q > 0 ? q : 1;
  }

  /* ---------- screen-reader announcements ---------- */

  var liveEl = null;
  function announcer() {
    if (liveEl) return liveEl;
    liveEl = document.createElement('div');
    liveEl.className = 'sr-qa-live visually-hidden';
    liveEl.setAttribute('aria-live', 'polite');
    liveEl.setAttribute('role', 'status');
    liveEl.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0';
    document.body.appendChild(liveEl);
    return liveEl;
  }
  function announce(text) {
    var el = announcer();
    el.textContent = '';
    setTimeout(function () { el.textContent = text; }, 50);
  }

  /* ---------- toast (errors, decklist summary) ---------- */

  var toast = (function () {
    var el, timer;
    function ensure() {
      if (el) return el;
      el = document.createElement('div');
      el.className = 'sr-qa-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
      return el;
    }
    return {
      show: function (text, opts) {
        opts = opts || {};
        var t = ensure();
        t.textContent = '';
        var span = document.createElement('span');
        span.textContent = text;
        t.appendChild(span);
        if (opts.viewCart) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'sr-qa-toast__link';
          b.textContent = STR.viewCart || 'View cart';
          b.addEventListener('click', function (e) { e.stopPropagation(); t.classList.remove('is-shown'); openDrawer(); });
          t.appendChild(b);
        }
        t.classList.toggle('is-error', !!opts.error);
        t.classList.remove('is-shown');
        void t.offsetWidth;
        t.classList.add('is-shown');
        clearTimeout(timer);
        timer = setTimeout(function () { t.classList.remove('is-shown'); }, opts.ms || 3500);
      }
    };
  })();

  /* ---------- where the image flies to: the header cart icon on screen ---------- */

  function cartTarget() {
    var icons = document.querySelectorAll('[data-cart-sidebar]');
    for (var i = 0; i < icons.length; i++) {
      var a = icons[i];
      if (!a.offsetParent) continue;
      var r = a.getBoundingClientRect();
      if (r.width > 0 && r.top >= -4 && r.bottom <= window.innerHeight + 4) return a;
    }
    return null;
  }

  /* ---------- drawer: opened by the customer, refreshed only when stale ---------- */

  function openDrawer() {
    // From quick view: close it first so closing the drawer returns to the page, not the pop-up.
    if (document.body.classList.contains('quick-view-show')) {
      var qvClose = document.querySelector('[data-close-quick-view-popup]');
      if (qvClose) qvClose.click();
    }
    var icon = document.querySelector('[data-cart-sidebar]');
    if (icon) icon.click();
    else window.location.href = ROOT + 'cart';
  }

  // Ella's own loading cover for the drawer (the same markup updateSidebarCart prepends). It covers
  // the old copy, including its Checkout button, until the new copy replaces the wrapper's HTML.
  var DRAWER_LOADING = '<div class="loading-overlay loading-overlay--custom sr-qa-drawer-loading"><div class="loading-overlay__spinner">' +
    '<svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
    '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div></div>';

  function drawerWrapper() { return document.querySelector('#halo-cart-sidebar .previewCart-wrapper'); }

  function coverDrawer() {
    var w = drawerWrapper();
    if (!w || w.querySelector('.sr-qa-drawer-loading')) return;
    w.classList.add('is-loading');
    w.insertAdjacentHTML('afterbegin', DRAWER_LOADING);
  }

  function uncoverDrawer() {
    var w = drawerWrapper();
    if (!w) return;
    var c = w.querySelector('.sr-qa-drawer-loading');
    if (c) { c.remove(); w.classList.remove('is-loading'); }
  }

  // Wait for queued adds, read the cart, and have Ella redraw the drawer. The old copy stays
  // covered meanwhile, so nothing in it (Checkout, express buttons) can be clicked.
  var drawerJob = null;
  function refreshDrawer() {
    var render = window.sharedFunctions && window.sharedFunctions.updateSidebarCart;
    if (!render) return Promise.resolve();
    coverDrawer();
    if (drawerJob) return drawerJob;
    drawerJob = whenIdle().then(getCart).then(function (cart) {
      return new Promise(function (resolve) {
        // Done when Ella has put its new HTML in: that replaces the wrapper's content, our cover
        // with it. (Not on cart-update: quick-add sends one of its own before Ella has finished.)
        var w = drawerWrapper();
        var cover = w && w.querySelector('.sr-qa-drawer-loading');
        var done = false, mo = null;
        function finish() { if (done) return; done = true; if (mo) mo.disconnect(); resolve(); }
        if (cover && 'MutationObserver' in window) {
          mo = new MutationObserver(function () { if (!cover.isConnected) finish(); });
          mo.observe(w, { childList: true });
        }
        setTimeout(finish, cover ? 8000 : 1500);
        render(cart);
      });
    }).catch(function () {}).then(function () {
      drawerJob = null;
      uncoverDrawer();   // only still there if Ella's request failed
    });
    return drawerJob;
  }

  // Capture phase: runs before Ella's click handler on the icon (which opens the drawer).
  document.addEventListener('click', function (e) {
    var icon = e.target.closest && e.target.closest('[data-cart-sidebar]');
    if (!icon) return;
    if (!state.drawerSeen && !busy()) {
      state.drawerSeen = true;   // Ella renders it now
      state.drawerDirty = false;
      return;
    }
    state.drawerSeen = true;
    if (!state.drawerDirty && !busy()) return;
    state.drawerDirty = false;
    refreshDrawer();
  }, true);

  /* ---------- "✓ n in cart" line ---------- */
  // Cards: inside the price row (right side), so the button never moves while someone is
  // clicking fast. Product page / quick view: its own line under the button row.

  var CHECK = '<svg class="sr-qa-incart__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M6.2 11.3 2.9 8l-1 1 4.3 4.3 8-8-1-1z"/></svg>';

  function holderFor(btn) {
    var card = btn.closest('.product-item');
    if (card) {
      var price = card.querySelector('.card-price');
      return price ? { el: price, where: 'beforeend', kind: 'card' } : null;
    }
    // .pvGroup-row is a flex row (quantity + button) on wider screens: go after the whole row.
    var row = btn.closest('.pvGroup-row') || btn.closest('.productView-groupTop') || btn.closest('.productView-action') || btn.closest('.product-form__buttons');
    if (row && !btn.closest('.productView-stickyCart')) return { el: row, where: 'afterend', kind: 'page' };
    return null;
  }

  var badges = {
    set: function (btn) {
      if (!btn.offsetParent) return;   // cards carry a second, hidden button
      var h = holderFor(btn);
      if (!h) return;
      var line = h.kind === 'card' ? h.el.querySelector(':scope > .sr-qa-incart')
        : (h.el.nextElementSibling && h.el.nextElementSibling.classList.contains('sr-qa-incart') ? h.el.nextElementSibling : null);
      var id = variantIdOf(btn);
      var q = id ? state.qty[id] || 0 : 0;
      state.jobs.forEach(function (j) { if (j.id === id) q += j.shown; });   // optimistic, like the count
      if (!q) { if (line) line.remove(); return; }
      if (!line) {
        line = document.createElement(h.kind === 'card' ? 'span' : 'p');
        line.className = 'sr-qa-incart sr-qa-incart--' + h.kind;
        h.el.insertAdjacentElement(h.where, line);
        if (h.kind === 'card') h.el.classList.add('sr-qa-has-incart');
      }
      if (line.getAttribute('data-q') === String(q)) return;   // unchanged: no DOM write
      var grew = parseInt(line.getAttribute('data-q') || '0', 10) < q;
      line.setAttribute('data-q', q);
      var label = (h.kind === 'card' ? (STR.inCartShort || '[count] in cart') : (STR.inCart || '[count] in your cart')).replace('[count]', q);
      line.innerHTML = CHECK + '<span></span>';
      line.lastChild.textContent = label;
      if (h.kind === 'page') {
        var link = document.createElement('button');
        link.type = 'button';
        link.className = 'sr-qa-incart__link';
        link.textContent = STR.viewCart || 'View cart';
        link.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); openDrawer(); });
        line.appendChild(link);
      }
      if (grew && line.animate && !reduceMotion) {
        line.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.12)' }, { transform: 'scale(1)' }], { duration: 300, easing: 'ease-out' });
      }
    },
    refreshAll: function (root) {
      (root || document).querySelectorAll('button[data-btn-addtocart]').forEach(badges.set);
    }
  };

  // Variant pickers change the hidden id; follow them.
  document.addEventListener('change', function (e) {
    var form = e.target && e.target.closest && e.target.closest('form');
    if (!form) return;
    setTimeout(function () { badges.refreshAll(form); }, 50);
  });

  /* ---------- fly to cart ---------- */

  var BOUNDARY = '.product-item, .productView-stickyCart, .productView, .quickView, .halo-popup-content, .shopify-section';

  // Largest visible image in the nearest container around the button that has one.
  function sourceImage(btn) {
    for (var el = btn.parentElement; el && el !== document.body; el = el.parentElement) {
      var best = null, area = 0;
      el.querySelectorAll('img').forEach(function (img) {
        var r = img.getBoundingClientRect();
        if (r.width < 30 || r.height < 30 || r.bottom < 0 || r.top > window.innerHeight) return;
        if (!(img.currentSrc || img.src)) return;
        if (r.width * r.height > area) { area = r.width * r.height; best = img; }
      });
      if (best) return best;
      if (el.matches(BOUNDARY)) return null;
    }
    return null;
  }

  function bump(target) {
    if (!target || !target.animate || reduceMotion) return;
    target.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(.95)' }, { transform: 'scale(1)' }],
      { duration: 420, easing: 'ease-out' }
    );
  }

  // Resolves with the target when the image lands (straight away when there is nothing to animate).
  function fly(btn) {
    var target = cartTarget();
    var img = sourceImage(btn);
    if (!target || !img || reduceMotion || !document.body.animate) return Promise.resolve(target);

    var from = img.getBoundingClientRect();
    var to = target.getBoundingClientRect();
    var w = Math.min(Math.max(from.width, 60), 150);
    var h = w * (from.height / from.width || 1.4);
    var startX = from.left + from.width / 2 - w / 2;
    var startY = from.top + from.height / 2 - h / 2;
    var dx = to.left + to.width / 2 - (startX + w / 2);
    var dy = to.top + to.height / 2 - (startY + h / 2);

    // Outer box moves on X, inner on Y with a lift first: a throw into the cart, not a slide.
    var outer = document.createElement('div');
    outer.className = 'sr-qa-fly';
    outer.style.cssText = 'left:' + startX + 'px;top:' + startY + 'px;width:' + w + 'px;height:' + h + 'px';
    var inner = document.createElement('div');
    inner.className = 'sr-qa-fly__inner';
    var clone = document.createElement('img');
    clone.src = img.currentSrc || img.src;
    clone.alt = '';
    inner.appendChild(clone);
    outer.appendChild(inner);
    document.body.appendChild(outer);

    var duration = Math.round(Math.min(800, Math.max(500, Math.hypot(dx, dy) * 0.6)));
    var lift = Math.min(120, Math.abs(dy) * 0.25 + 40);
    var a = outer.animate(
      [{ transform: 'translateX(0)' }, { transform: 'translateX(' + dx + 'px)' }],
      { duration: duration, easing: 'cubic-bezier(.35,0,.65,1)', fill: 'forwards' }
    );
    inner.animate(
      [
        { transform: 'translateY(0) scale(1)', opacity: 1 },
        { transform: 'translateY(' + -lift + 'px) scale(.75)', opacity: 1, offset: 0.3 },
        { transform: 'translateY(' + dy + 'px) scale(.12)', opacity: 0.7 }
      ],
      { duration: duration, easing: 'cubic-bezier(.5,0,.85,.55)', fill: 'forwards' }
    );
    return new Promise(function (resolve) {
      var done = false;
      function finish() { if (done) return; done = true; outer.remove(); resolve(target); }
      a.onfinish = finish;
      setTimeout(finish, duration + 150);
    });
  }

  /* ---------- cart writes ---------- */

  function readProperties(btn, form) {
    var props = {}, hasFile = false;
    var fields = Array.prototype.slice.call(form.querySelectorAll('[name^="properties["]'));
    // Product page: Ella keeps line-item property fields outside the form.
    var scope = btn.closest('.productView, .quickView, .halo-popup-content');
    if (scope) {
      scope.querySelectorAll('.line-item-property__field [name^="properties["]').forEach(function (f) {
        if (fields.indexOf(f) < 0) fields.push(f);
      });
    }
    fields.forEach(function (f) {
      var key = f.name.slice(11, -1);
      if (f.type === 'file') { if (f.files && f.files[0]) hasFile = true; return; }
      if ((f.type === 'checkbox' || f.type === 'radio') && !f.checked) return;
      if (f.value === '') return;
      props[key] = f.value;
    });
    return { props: props, hasFile: hasFile };
  }

  function makeJob(btn) {
    var form = formOf(btn);
    var p = readProperties(btn, form);
    var job = {
      btn: btn,
      id: variantIdOf(btn),
      qty: quantityOf(btn),
      props: p.props,
      plain: !Object.keys(p.props).length,
      formData: null,
      shown: 0,
      landed: false,
      result: null
    };
    if (p.hasFile) {
      job.formData = new FormData(form);
      var scope = btn.closest('.productView, .quickView, .halo-popup-content');
      if (scope) {
        scope.querySelectorAll('.line-item-property__field [name^="properties["]').forEach(function (f) {
          if (f.form === form) return;
          if (f.type === 'file') { if (f.files[0]) job.formData.append(f.name, f.files[0]); }
          else if ((f.type !== 'checkbox' && f.type !== 'radio') || f.checked) job.formData.append(f.name, f.value);
        });
      }
    }
    var sp = form.querySelector('[name="selling_plan"]');
    if (sp && sp.value) job.sellingPlan = sp.value;
    return job;
  }

  function item(job, qty) {
    var it = { id: job.id, quantity: qty == null ? job.qty : qty };
    if (!job.plain) it.properties = job.props;
    if (job.sellingPlan) it.selling_plan = job.sellingPlan;
    return it;
  }

  function errorText(data) {
    return (data && (data.description || data.message)) || STR.error || 'Could not add this item. Please try again.';
  }

  function post(body, isJson, keepalive) {
    // keepalive: an add already on its way still lands if the customer leaves the page (e.g.
    // clicks Checkout) before Shopify answers. Not for file uploads (keepalive bodies max 64 KB).
    return fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: headers(isJson),
      credentials: 'same-origin',
      keepalive: !!keepalive || !!isJson,
      body: isJson ? JSON.stringify(body) : body
    }).then(function (r) {
      return r.json().catch(function () { return null; }).then(function (data) {
        return { ok: r.ok && data && !data.status, data: data };
      });
    });
  }

  /* Pify (order limit) checks an add against its own copy of the cart. That copy is refreshed only
     by the /cart.js read Pify itself makes right after each add (not by our reads). When clicks are
     queued, our next add used to go out a few ms after that read answered, before Pify had stored
     it, so the add was checked against the cart from one add earlier: 5 clicks, limit 4, 5 in the
     cart (seen live 2026-10-19). So with Pify on the page, the next add waits for Pify's read
     (both /cart.js reads after the add: ours and Pify's) plus a moment to parse it. */
  function pifyOnPage() {
    try { return /pol_/.test(Function.prototype.toString.call(window.fetch)); } catch (e) { return false; }
  }
  function cartReadsWatcher() {
    if (!pifyOnPage() || !('PerformanceObserver' in window)) return null;
    var seen = 0, want = null, done = null, po;
    try {
      po = new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (en) { if (/\/cart\.js(\?|$)/.test(en.name)) seen++; });
        if (want && seen >= 2) want();
      });
      po.observe({ type: 'resource' });
    } catch (e) { return null; }
    return {
      // Resolves once two cart reads have finished since the add went out (or after 1.5 s).
      settled: function () {
        if (done) return done;
        done = new Promise(function (resolve) {
          var over = false;
          function finish() { if (over) return; over = true; try { po.disconnect(); } catch (e) {} setTimeout(resolve, 60); }
          want = finish;
          if (seen >= 2) finish();
          setTimeout(finish, 1500);
        });
        return done;
      }
    };
  }

  // Always the single-item form {id, quantity}. The Pify order-limit app only checks that form
  // (it lowers an over-limit add to 0 and shows its own pop-up); an items[] batch would slip past
  // it. (Takes a list so a group could share one request; pump() sends one.)
  function sendGroup(jobs) {
    var job = jobs[0];
    var qty = jobs.reduce(function (n, j) { return n + j.qty; }, 0);
    var before = 0;
    var msg = null, failed = null;
    // "before" must be a confirmed number: if the first cart read failed, read it now, or an add
    // Pify turned down would be counted as added.
    var ready = state.serverCount == null ? getCart().then(applyCart) : Promise.resolve();
    var pify = null;
    return ready.then(function () {
      before = state.qty[job.id] || 0;
      pify = cartReadsWatcher();
      return job.formData ? post(job.formData, false) : post(item(job, qty), true);
    }).then(function (res) {
      msg = res.data && res.data.description;
      if (!res.ok) failed = errorText(res.data);
      // Read the cart: it tells us what really landed (stock caps, Pify lowering the add to 0,
      // a 422 that still added some). Then, with Pify on the page, wait for Pify's own read
      // (see cartReadsWatcher) before the next add goes out.
      return getCart();
    }).then(function (cart) {
      return pify ? pify.settled().then(function () { return cart; }) : cart;
    }).then(function (cart) {
      var after = qtyMap(cart);
      state.qty = after;
      state.serverCount = cart.item_count;
      var total = Math.max(0, Math.min(qty, (after[job.id] || 0) - before));
      if (!total && failed && !((after[job.id] || 0) > 0)) {
        jobs.forEach(function (j) { j.result = { error: failed }; });
        return;
      }
      var left = total;
      jobs.forEach(function (j) {
        var got = Math.min(j.qty, left);
        left -= got;
        j.result = { added: got, msg: msg };
      });
    });
  }

  function pump() {
    if (state.inflight || !state.waiting.length) {
      if (!busy()) {
        var w = state.idleWaiters.splice(0);
        w.forEach(function (fn) { fn(); });
      }
      return;
    }
    state.inflight = true;
    // One click, one request. Merging clicks would be faster, but Pify refuses an add as a whole:
    // six quick clicks merged into "+5" past a limit of 4 would leave 1 in the cart, not 4.
    var group = [state.waiting.shift()];
    sendGroup(group).catch(function () {
      group.forEach(function (j) { if (!j.result) j.result = { error: STR.network || 'Connection problem. Please try again.' }; });
    }).then(function () {
      state.inflight = false;
      state.drawerDirty = true;
      try { group.forEach(settle); } catch (e) {}
      pump();
      maybeSync();
    });
  }

  // The order-limit app shows its own pop-up when it refuses an add; do not stack ours on it.
  function limitPopupShown() {
    var els = document.querySelectorAll('[class^="pol-"], [class*=" pol-"]');
    for (var i = 0; i < els.length; i++) {
      if (els[i].offsetParent && /maximum|minimum|limit/i.test(els[i].textContent || '')) return true;
    }
    return false;
  }

  // Both the request and the flight are done: show the outcome on the button.
  function settle(job) {
    if (!job.landed || !job.result) return;
    var i = state.jobs.indexOf(job);
    if (i < 0) return;
    state.jobs.splice(i, 1);
    var r = job.result;
    renderCount();
    badges.refreshAll();
    if (r.error) {
      toast.show(r.error, { error: true });
    } else if (!r.added) {
      var inCart = state.qty[job.id] || 0;
      setTimeout(function () {
        if (limitPopupShown()) return;
        toast.show(r.msg || (STR.noMore || 'All [count] we have are already in your cart.').replace('[count]', inCart), { error: true });
      }, 700);
    } else if (r.added < job.qty) {
      toast.show((STR.partial || 'Only [added] could be added. The [count] in your cart is all we have.')
        .replace('[added]', r.added).replace('[count]', state.qty[job.id] || 0), { error: true });
    }
    if (r.added) announce((STR.addedSr || 'Added to cart. [count] in your cart.').replace('[count]', state.qty[job.id] || r.added));
    maybeSync();
  }

  // Everything sent and landed: tell the rest of the theme (drawer free-shipping bar, Clear cart).
  function maybeSync() {
    if (!busy() && !state.jobs.length) syncSoon();
  }

  var syncTimer;
  // After a burst, read the real cart once and tell the rest of the theme (free-shipping bar etc.).
  function syncSoon() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      if (busy() || state.jobs.length) return;
      getCart().then(function (cart) {
        if (busy() || state.jobs.length) return;
        applyCart(cart);
        document.dispatchEvent(new CustomEvent('cart-update', { detail: cart }));
      }).catch(function () {});
    }, 400);
  }

  // A double-click is one add (Ella used to block the second click while loading). Only the
  // customer's own clicks count here: Pify's relay clicks (below) come in bursts after its check.
  // 250 ms: a double-click is faster than that; quick deliberate clicks are usually slower.
  function isDoubleClick(btn) {
    var now = Date.now();
    var dbl = !!btn._srQaLast && now - btn._srQaLast < 250;
    btn._srQaLast = now;
    return dbl;
  }

  // Pify (order limit) hides the product page's add button and shows its own copy (.pol). A click
  // on the copy makes Pify read the cart and check its limit, then it clicks the hidden original.
  // Anything shown on screen (flight, busy state, "in cart" line) uses the visible copy.
  function visibleTwin(btn) {
    if (btn.offsetParent || !btn.parentElement) return btn;
    var twin = btn.parentElement.querySelector('.pol[data-btn-addtocart]');
    return twin && twin.offsetParent ? twin : btn;
  }

  function add(btn) {
    var shownBtn = visibleTwin(btn);
    // Mobile product page: the sticky bar opens an options sheet; close it like Ella did.
    if (document.body.classList.contains('show-mobile-options')) document.body.classList.remove('show-mobile-options');
    var job = makeJob(btn);
    state.jobs.push(job);
    state.waiting.push(job);
    ensureLoaded().then(pump);

    shownBtn.classList.add('sr-qa-busy');
    fly(shownBtn).then(function (target) {
      job.landed = true;
      shownBtn.classList.remove('sr-qa-busy');
      if (job.result) {
        // Shopify already answered; settle() shows the real outcome.
        if (job.result.added) bump(target);
      } else {
        // Optimistic: count up and "✓ Added" when the image lands.
        job.shown = job.qty;
        renderCount();
        badges.set(shownBtn);
        bump(target);
      }
      settle(job);
    });
  }

  // Leaving the page with clicks still queued: send them anyway.
  window.addEventListener('pagehide', function () {
    var rest = state.waiting.filter(function (j) { return !j.formData; });
    state.waiting = [];
    rest.forEach(function (j) { try { post(item(j), true, true); } catch (e) {} });
  });

  function shouldHandle(btn) {
    if (!btn || btn.tagName !== 'BUTTON') return false;
    if (btn.disabled || btn.classList.contains('is-notify-me') || btn.classList.contains('is-soldout')) return false;
    if (document.body.classList.contains('template-cart')) return false;
    var form = formOf(btn);
    if (!form || !variantIdOf(btn)) return false;
    if (form.querySelector('.recipient-form') || document.querySelector('.recipient-form')) return false;
    return true;
  }

  // Capture on window: runs before Ella's delegated jQuery handler and the form submit.
  window.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-btn-addtocart]');
    if (!btn) return;
    if (btn.classList.contains('pol')) {
      // Pify's copy: let Pify check the order limit; it then clicks the original (handled below).
      // A double-click on it is still one add.
      if (e.isTrusted && isDoubleClick(btn)) { e.preventDefault(); e.stopImmediatePropagation(); }
      return;
    }
    if (!shouldHandle(btn)) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.isTrusted && isDoubleClick(btn)) return;
    add(btn);
  }, true);

  // Enter in a quantity box submits the form; route it the same way.
  // Only cart forms: the header search <form> wraps the predictive-search results, which
  // contain product cards with their own add buttons. Without this check, Enter / the
  // magnifier in search added the first result to the cart instead of searching.
  function isCartForm(form) {
    var action = form && form.getAttribute && form.getAttribute('action');
    return !!action && /\/cart\/add(\.js)?\/?(\?|$)/.test(action);
  }
  window.addEventListener('submit', function (e) {
    var form = e.target;
    if (!isCartForm(form)) return;
    var btn = form.querySelector('button[data-btn-addtocart]:not(.pol)');
    if (!btn || formOf(btn) !== form) return;
    if (!shouldHandle(btn)) return;
    e.preventDefault();
    e.stopPropagation();
    // With Pify's copy on the page, go through it so the order limit is checked first.
    var pol = form.querySelector('.pol[data-btn-addtocart]');
    if (pol && pol.offsetParent && !pol.disabled) { pol.click(); return; }
    add(btn);
  }, true);

  /* ---------- other add paths ---------- */

  // Ella's own drawer re-render (quantity +/- or remove in the drawer, bundles): follow the numbers.
  document.addEventListener('cart-update', function (e) {
    if (e.detail && typeof e.detail.item_count === 'number' && !busy() && !state.jobs.length) applyCart(e.detail);
  });

  // Decklist Search adds in bulk and announces it.
  document.addEventListener('cart:updated', function (e) {
    var cart = e.detail && e.detail.cart;
    if (cart && typeof cart.item_count === 'number' && !busy()) applyCart(cart);
    state.drawerDirty = true;
    if (e.detail && e.detail.source === 'decklist') {
      toast.show(STR.decklistAdded || 'Cards added to your cart.', { viewCart: true, ms: 6000 });
    }
  });

  /* ---------- sticky bar search: Esc and a click on the dimmed page close it ---------- */

  function stickySearch() {
    // This search icon only exists in the sticky bar, so an open one is the sticky search.
    return document.querySelector('.section-header-navigation .header__search details[open]');
  }
  // Same clean-up as Ella's own click-outside close (theme.js hideSearchSticky); its × button
  // only removes [open] and leaves the bar in "search open" state.
  function closeStickySearch(d) {
    var header = d.closest('.section-header-navigation');
    document.querySelectorAll('[class*="section-header-"]').forEach(function (el) { el.classList.remove('sticky-search-menu-open'); });
    if (header) header.style.zIndex = header.getAttribute('data-index') || '';
    document.querySelectorAll('.header-navigation .search_details[open]').forEach(function (el) { el.removeAttribute('open'); });
    var res = d.querySelector('.quickSearchResultsWrap.is-show');
    if (res) { res.classList.remove('is-show'); res.classList.add('hidden'); }
    var icon = d.querySelector('summary');
    if (icon) icon.focus({ preventScroll: true });
  }
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var d = stickySearch();
    if (!d) return;
    var input = d.querySelector('input[type="search"]');
    if (input && input.value) return;          // first Esc clears the text (browser default)
    closeStickySearch(d);
  });
  // Ella focuses the main header's box (scrolled away); put the cursor in the bar's box instead.
  document.addEventListener('toggle', function (e) {
    var d = e.target;
    if (!d.open || !d.closest || !d.closest('.section-header-navigation .header__search')) return;
    setTimeout(function () {
      var input = d.querySelector('input[type="search"]');
      if (input) input.focus({ preventScroll: true });
    }, 80);
  }, true);
  // Capture phase: Ella's own × handler (bound on the button) removes [open] first otherwise,
  // and then this would no longer find the open search to clean up.
  document.addEventListener('click', function (e) {
    var d = stickySearch();
    if (!d) return;
    var onBackdrop = e.target.classList && e.target.classList.contains('search-modal') && d.contains(e.target);
    var onClose = e.target.closest && e.target.closest('.header-search-close') && d.contains(e.target);
    if (onBackdrop || onClose) closeStickySearch(d);
  }, true);

  /* ---------- start ---------- */

  function start() {
    state.baseCount = readCount();
    announcer();
    if ('requestIdleCallback' in window) requestIdleCallback(ensureLoaded, { timeout: 2500 });
    else setTimeout(ensureLoaded, 800);

    // Cards rendered later (Singles filter, load more, quick view) get their badge too.
    if ('MutationObserver' in window) {
      var queued = false;
      new MutationObserver(function (list) {
        if (queued) return;
        for (var i = 0; i < list.length && !queued; i++) {
          var nodes = list[i].addedNodes;
          for (var k = 0; k < nodes.length; k++) {
            var n = nodes[k];
            if (n.nodeType === 1 && !/(^| )sr-qa-/.test(n.className || '') && !(list[i].target.className || '').toString().match(/sr-qa-/)) { queued = true; break; }
          }
        }
        if (!queued) return;
        setTimeout(function () { queued = false; badges.refreshAll(); }, 150);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  // Back/forward cache or another tab: the cart may have changed elsewhere. If it did, the
  // drawer is out of date and the rest of the theme (PREORDER gate etc.) is told.
  function refreshIfIdle() {
    if (!state.loaded || busy() || state.jobs.length) return;
    var was = JSON.stringify(state.qty);
    getCart().then(function (cart) {
      if (busy() || state.jobs.length) return;
      applyCart(cart);
      if (JSON.stringify(state.qty) === was) return;
      state.drawerDirty = true;
      document.dispatchEvent(new CustomEvent('cart-update', { detail: cart }));
    }).catch(function () {});
  }
  // Back/forward cache: whatever was queued was sent on pagehide or is lost; start clean.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    state.jobs = [];
    state.waiting = [];
    state.inflight = false;
    getCart().then(applyCart).catch(function () {});
  });
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible') refreshIfIdle(); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.srQuickAdd = {
    add: add,
    openDrawer: openDrawer,
    refreshDrawer: refreshDrawer,
    busy: function () { return busy() || drawerJob != null; },
    whenIdle: function () { return whenIdle().then(function () { return drawerJob; }); }
  };
})();
